import { OptimizationTask } from '@idea2app/ai-work-order-service';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { Badge, ProgressBar } from 'react-bootstrap';

import { t } from '../model/Translation';

export interface TaskStep {
    title: string;
    summary?: string;
}

export interface TaskStepsProps<T extends OptimizationTask = OptimizationTask> {
    task: T;
    steps: TaskStep[];
    currentStep?: number;
}

@observer
export class TaskSteps<T extends OptimizationTask> extends Component<
    TaskStepsProps<T>
> {
    get statusBadge() {
        const { status } = this.props.task;

        return {
            completed: (
                <Badge bg="success" text="white" pill>
                    {t('completed')}
                </Badge>
            ),
            in_progress: (
                <Badge bg="primary" text="white" pill>
                    {t('in_analysis')}
                </Badge>
            ),
            failed: (
                <Badge bg="danger" text="white" pill>
                    {t('failed')}
                </Badge>
            )
        }[status];
    }

    renderStep = ({ title, summary }: TaskStep, index = 0) => {
        const { currentStep } = this.props;
        const isDone = currentStep !== undefined && index < currentStep,
            isDoing = currentStep === index;

        return (
            <li key={index}>
                <h3 className="fs-5 d-flex align-items-center gap-2">
                    <span
                        className="rounded-circle bg-primary text-white d-flex justify-content-center align-items-center"
                        style={{
                            width: '2rem',
                            height: '2rem',
                            filter:
                                !isDone && !isDoing ? 'grayscale(100%)' : 'none'
                        }}
                    >
                        {index + 1}
                    </span>
                    <span
                        className="text-primary"
                        style={{ filter: !isDone ? 'grayscale(100%)' : 'none' }}
                    >
                        {title}
                    </span>
                </h3>
                <p className="text-muted ms-4 ps-3">{summary}</p>
            </li>
        );
    };

    render() {
        const { task, steps, currentStep = 0 } = this.props;
        const { name, startedAt } = task;
        const percent = +((currentStep / steps.length) * 100).toFixed(1);

        return (
            <>
                <hgroup className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="fs-4">{name}</h2>
                        <time className="small text-gray">
                            {t('start_time')}：
                            {new Date(startedAt).toLocaleString()}
                        </time>
                    </div>
                    {this.statusBadge}
                </hgroup>
                <section className="d-flex flex-column gap-3 my-4">
                    <header className="d-flex justify-content-between align-items-center">
                        <span>
                            {
                                t('analysis_progress', {
                                    percent: 0,
                                    count: 0
                                }).split('：')[0]
                            }
                        </span>
                        <span>{percent}%</span>
                    </header>
                    <ProgressBar variant="danger" now={percent} />
                    <footer className="rounded bg-light p-3 d-flex justify-content-between align-items-center">
                        <span>{t('current_stage')}：</span>
                        <span>{t('estimated_remaining')}：</span>
                    </footer>
                </section>
                <ol className="list-unstyled">{steps.map(this.renderStep)}</ol>
            </>
        );
    }
}
